import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface WelcomeEmailTemplate {
  id: string;
  key: string;
  label: string;
  subject: string;
  body: string;
  is_active: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export type WelcomeEmailTemplateInsert = Omit<WelcomeEmailTemplate, "id" | "created_at" | "updated_at">;
export type WelcomeEmailTemplateUpdate = Partial<Omit<WelcomeEmailTemplate, "id" | "created_at" | "updated_at">>;

export function useWelcomeEmailTemplates() {
  return useQuery({
    queryKey: ["welcome-email-templates"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("welcome_email_templates")
        .select("*")
        .eq("is_active", true)
        .order("display_order");
      if (error) throw error;
      return data as WelcomeEmailTemplate[];
    },
  });
}

export function useAllWelcomeEmailTemplates() {
  return useQuery({
    queryKey: ["welcome-email-templates", "all"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("welcome_email_templates")
        .select("*")
        .order("display_order");
      if (error) throw error;
      return data as WelcomeEmailTemplate[];
    },
  });
}

export function useCreateWelcomeEmailTemplate() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (template: WelcomeEmailTemplateInsert) => {
      const { data, error } = await supabase
        .from("welcome_email_templates")
        .insert(template)
        .select()
        .single();
      if (error) throw error;
      return data as WelcomeEmailTemplate;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["welcome-email-templates"] });
      toast({ title: "Template created successfully" });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to create template",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export function useUpdateWelcomeEmailTemplate() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: WelcomeEmailTemplateUpdate }) => {
      const { data, error } = await supabase
        .from("welcome_email_templates")
        .update(updates)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data as WelcomeEmailTemplate;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["welcome-email-templates"] });
      toast({ title: "Template updated successfully" });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to update template",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export function useDeleteWelcomeEmailTemplate() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("welcome_email_templates")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["welcome-email-templates"] });
      toast({ title: "Template deleted successfully" });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to delete template",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}
